import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Newspaper, Loader2, Sparkles, Bug, Heart, Stethoscope, Megaphone, Pin, CheckCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate } from '../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

export const CATEGORY_META = {
  new_feature: { label: 'New Feature', icon: Sparkles, color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  medical_resource: { label: 'Medical Resource', icon: Stethoscope, color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  community: { label: 'Community', icon: Megaphone, color: 'bg-pink-100 text-pink-700', dot: 'bg-pink-500' },
  bug_fix: { label: 'Bug Fix', icon: Bug, color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  awareness: { label: 'Awareness', icon: Heart, color: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
};

// Tiny markdown helper — bold, italic, line breaks. Safe (escapes HTML first).
export const renderMarkdown = (text) => {
  if (!text) return '';
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br />');
};

const NewsPage = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/news`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(res.data.posts || []);
    } catch (e) {
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPosts();
    // Mark as read when the user visits the page
    axios.post(`${API}/api/news/mark-read`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => { /* silent */ });
  }, [fetchPosts, token]);

  const fmtDate = (s) => s
    ? formatLocalDate(s, undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  return (
    <div className="space-y-6" data-testid="news-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-100 rounded-xl p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
            <Newspaper className="w-8 h-8 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-nunito font-bold text-[#2D2D2D] mb-2">
              What's New
            </h1>
            <p className="text-[#57534E] max-w-2xl">
              Stay up to date on new features, bug fixes, medical resources, and community moments
              from the Calm Pathways team.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : posts.length === 0 ? (
        <Card className="p-12 text-center">
          <Newspaper className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <p className="text-[#57534E] font-medium">No posts yet</p>
          <p className="text-sm text-stone-500 mt-1">
            Check back soon — we ship updates regularly.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => {
            const meta = CATEGORY_META[post.category] || CATEGORY_META.new_feature;
            const Icon = meta.icon;
            return (
              <Card key={post.id} className="p-6" data-testid={`news-post-${post.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 ${meta.color}`}>
                      <Icon className="w-3 h-3" />
                      {meta.label}
                    </span>
                    {post.is_pinned && (
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-stone-100 text-stone-600 flex items-center gap-1">
                        <Pin className="w-3 h-3" />
                        Pinned
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-stone-500">{fmtDate(post.published_at || post.created_at)}</span>
                </div>
                <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">
                  {post.title}
                </h2>
                <div
                  className="text-[#57534E] leading-relaxed prose-sm"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(renderMarkdown(post.body), {
                      ALLOWED_TAGS: ['strong', 'em', 'br'],
                      ALLOWED_ATTR: [],
                    }),
                  }}
                />
                {post.author_name && (
                  <p className="text-xs text-stone-400 mt-3 italic">— {post.author_name}</p>
                )}
              </Card>
            );
          })}
          <div className="text-center py-4 text-stone-400 text-sm flex items-center justify-center gap-2">
            <CheckCheck className="w-4 h-4" /> You're all caught up
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
